import type { Board } from "../board";
import { DamageType } from "../damageType";
import type { SomethingWithState } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { EngineerAI, type EngineerActor, EngineerLogicState } from "../ai/engineerAI";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { XY } from "../xy";
import { EngineerCorpse } from "./engineerCorpse";

export class EngineerRobot extends Mob implements SomethingWithState, EngineerActor {
	static key = "EngineerRobot";

	private logicState: EngineerLogicState = EngineerLogicState.WANDERING;
	private repairTurnsLeft: number = 0;
	private ai: EngineerAI;

	constructor(xy: XY) {
		super(xy);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.ai = new EngineerAI(this);
		this.ai.fleeSoundSupplier = () => new Sound(SoundId.ROBOT_TARGET_ACQUIRED, this.xy);
		this.ai.repairSoundSupplier = () => new Sound(SoundId.ROBOT_POWER_ON, this.xy);
		this.ai.wanderSoundSupplier = () => new Sound(SoundId.ROBOT_SOUND, this.xy);
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	getState(): string {
		return this.logicState === EngineerLogicState.REPAIRING ? "repairing" : "normal";
	}

	getLogicState(): EngineerLogicState {
		return this.logicState;
	}

	setLogicState(state: EngineerLogicState): void {
		this.logicState = state;
	}

	getRepairTurnsLeft(): number {
		return this.repairTurnsLeft;
	}

	setRepairTurnsLeft(n: number): void {
		this.repairTurnsLeft = n;
	}

	generateCorpse(): PhysicalThing | null {
		return new EngineerCorpse(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.ROBOT_DIE);
	}

	processMobAction(_board: Board): void {
		this.ai.processTurn();
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			logicState: this.logicState,
			repairTurnsLeft: this.repairTurnsLeft,
			ai: this.ai.serialize(),
		};
	}

	static deserialize(data: any): EngineerRobot {
		const m = new EngineerRobot(deserializeXY(data.xy));
		m.restoreMobFields(data);
		m.logicState = data.logicState ?? EngineerLogicState.WANDERING;
		m.repairTurnsLeft = data.repairTurnsLeft ?? 0;
		m.ai.restoreFrom(data.ai);
		return m;
	}
}
